import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { LocalHospital, Add, Save, UnfoldMore } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { medicosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const GrillaMedicos = () => {
  const [medicos, setMedicos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [idUsuario, setIdUsuario] = useState('');
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(medicos);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [rMedicos, rUsuarios] = await Promise.all([
        medicosAPI.getMedicos(),
        medicosAPI.getUsuariosPorRol(4),
      ]);
      setMedicos(rMedicos.data || []);
      setUsuarios(rUsuarios.data || []);
    } catch {
      setToast({ message: 'Error al cargar los datos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = sortedData.filter(m =>
    textMatch(m.medico1, buscar)
  );
  const totalPages = Math.ceil(filtrados.length / PAGE_SIZE);
  const paginados = filtrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleNuevo = () => { setEditando(null); setNombre(''); setIdUsuario(''); setMostrarForm(true); };
  const handleEditar = (m) => { setEditando(m); setNombre(m.medico1); setIdUsuario(m.idUsuario ? String(m.idUsuario) : ''); setMostrarForm(true); };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editando) {
        await medicosAPI.actualizarMedico(editando.idMedico, nombre.trim(), parseInt(idUsuario));
        setToast({ message: 'Médico actualizado con éxito.', type: 'success' });
      } else {
        await medicosAPI.guardarMedico(nombre.trim(), parseInt(idUsuario));
        setToast({ message: 'Médico guardado con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch {
      setToast({ message: 'Error al guardar el médico.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await medicosAPI.eliminarMedico(confirmEliminar);
      setToast({ message: 'Médico eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar el médico.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <LocalHospital sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Médicos
          </h2>
          <span className="page-subtitle">Administración de médicos</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar}>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Médico' : 'Nuevo Médico'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.5rem' }}>
              <Form.Group className="mb-3">
                <Form.Label>* Médico</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={100}
                  placeholder="Nombre del médico..."
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                  autoFocus
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>* Usuario</Form.Label>
                <Form.Select
                  value={idUsuario}
                  onChange={(e) => setIdUsuario(e.target.value)}
                  required
                >
                  <option value="">Seleccione un usuario...</option>
                  {usuarios.map(u => (
                    <option key={u.id} value={u.id}>{u.usuario1}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !nombre.trim() || !idUsuario}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Médico</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar médico..."
                  value={buscar}
                  onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Resultados</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{filtrados.length} médicos encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Médico
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idMedico')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idMedico') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('medico1')} style={{ cursor: 'pointer' }}>
                    Médico {getSortIcon('medico1') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="3" className="empty-state">{loading ? 'Cargando...' : 'No hay médicos para mostrar'}</td></tr>
                ) : (
                  paginados.map((m) => (
                    <tr key={m.idMedico}>
                      <td>{m.idMedico}</td>
                      <td>{m.medico1}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(m)} onDelete={() => setConfirmEliminar(m.idMedico)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar médico"
        message="¿Estás seguro que querés eliminar este médico?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaMedicos;
